import React from 'react'
import { Box, Tab, Tabs } from '@mui/material'
import useRouter from '../hooks/useRouter'
import useAccount from '../hooks/useAccount'
import AutoCoderPage from './AutoCoderPage'
import { FrameworkLibraryView } from './FrameworkLibraryPage'
import ActivityTab from '../components/autocoder/ActivityTab'
import DataTab from '../components/autocoder/data/DataTab'

/**
 * The AutoCoder shell: one page, four tabs.
 *
 * The tab lives in the URL (`?tab=`) so every view is linkable, and only the
 * selected tab is mounted — the polling views (Activity's lane depth, the
 * receipts list) must not poll from behind another tab. Same contract as the
 * pipeline page's `?section=`.
 *
 * `pipelines` is the default so every existing `/autocoder/:customer/:job`
 * link keeps landing exactly where it used to.
 */
export type AutoCoderShellTab = 'data' | 'pipelines' | 'frameworks' | 'activity'

const SHELL_TABS: { id: AutoCoderShellTab; label: string }[] = [
  { id: 'data', label: 'Data' },
  { id: 'pipelines', label: 'Pipelines' },
  { id: 'frameworks', label: 'Code Frames' },
  { id: 'activity', label: 'Activity' },
]

export const resolveShellTab = (value?: string): AutoCoderShellTab => {
  const match = SHELL_TABS.find(t => t.id === value)
  return match ? match.id : 'pipelines'
}

const AutoCoderShellPage: React.FC = () => {
  const router = useRouter()
  const account = useAccount()
  const tab = resolveShellTab(router.params.tab)
  // Same resolution order the pipeline list uses: the URL is authoritative, the
  // account is the fallback for links that carry no scope.
  const customer = router.params.customer || account.customer?.name || ''
  const job = router.params.job || account.job?.name || ''

  const goToTab = (next: AutoCoderShellTab) => router.setParams({ tab: next })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}>
      <Tabs
        value={tab}
        onChange={(_, next: AutoCoderShellTab) => goToTab(next)}
        sx={{
          px: 2,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundColor: 'background.default',
        }}
      >
        {SHELL_TABS.map(t => (
          <Tab
            key={t.id}
            value={t.id}
            label={t.label}
            id={`autocoder-tab-${t.id}`}
            // Addressed by tab id, never by its label: the labels are copy and
            // change (Videos → Entities already happened once), the ids are the
            // URL contract.
            data-testid={`autocoder-shell-tab-${t.id}`}
          />
        ))}
      </Tabs>

      <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
        {tab === 'data' && <DataTab customer={customer} job={job} />}
        {tab === 'pipelines' && (
          <AutoCoderPage embedded onViewFrameworks={() => goToTab('frameworks')} />
        )}
        {tab === 'frameworks' && (
          <FrameworkLibraryView embedded onViewPipelines={() => goToTab('pipelines')} />
        )}
        {tab === 'activity' && <ActivityTab customer={customer} job={job} />}
      </Box>
    </Box>
  )
}

export default AutoCoderShellPage
